/**
 * Due Diligence System Types
 * 
 * Comprehensive type definitions for the composable due diligence
 * checklist system used during opportunity evaluation phase.
 */

// =============================================================================
// Flag Types
// =============================================================================

export type FlagType = 
  | 'big_red'      // 🚩🚩 Major concern - deal killer
  | 'red'          // 🚩 Significant concern
  | 'yellow'       // 🟡 Needs further inquiry
  | 'green'        // 🟢 Positive indicator
  | 'big_green'    // 🟢🟢 Major positive - value add
  | 'clear'        // ✅ Checked, no issues
  | 'pending'      // ⏳ Not yet evaluated
  | 'not_applicable'; // ➖ Does not apply

export const FLAG_CONFIG: Record<FlagType, {
  label: string;
  icon: string;      // Ionicons name
  iconDouble?: boolean; // Show two icons for emphasis
  color: string;
  priority: number; // Lower = more important (for sorting)
}> = {
  big_red: { label: 'Big Red Flag', icon: 'flag', iconDouble: true, color: '#DC2626', priority: 1 },
  red: { label: 'Red Flag', icon: 'flag', color: '#EF4444', priority: 2 },
  yellow: { label: 'Yellow Flag', icon: 'flag', color: '#EAB308', priority: 3 },
  green: { label: 'Green Flag', icon: 'flag', color: '#22C55E', priority: 5 },
  big_green: { label: 'Big Green Flag', icon: 'flag', iconDouble: true, color: '#16A34A', priority: 4 },
  clear: { label: 'Clear', icon: 'checkmark-circle', color: '#6B7280', priority: 6 },
  pending: { label: 'Pending', icon: 'ellipse-outline', color: '#9CA3AF', priority: 7 },
  not_applicable: { label: 'N/A', icon: 'remove-circle-outline', color: '#D1D5DB', priority: 8 },
};

// =============================================================================
// Status Types
// =============================================================================

export type ItemStatus = 'pending' | 'in_progress' | 'complete' | 'not_applicable';

export const STATUS_CONFIG: Record<ItemStatus, {
  label: string;
  color: string;
}> = {
  pending: { label: 'Pending', color: '#9CA3AF' },
  in_progress: { label: 'In Progress', color: '#3B82F6' },
  complete: { label: 'Complete', color: '#22C55E' },
  not_applicable: { label: 'N/A', color: '#D1D5DB' },
};

// =============================================================================
// Workflow Types
// =============================================================================

export type WorkflowPhase = 
  | 'setup'           // Configuring property type and modules
  | 'initial_screen'  // Quick assessment, obvious flags
  | 'deep_diligence'  // Thorough investigation
  | 'final_analysis'  // Financial modeling, final review
  | 'decision';       // Go/No-Go decision made

export type FinalDecision = 'go' | 'no_go' | 'pass_for_now';

export const PHASE_CONFIG: Record<WorkflowPhase, {
  label: string;
  description: string;
  order: number;
}> = {
  setup: { 
    label: 'Setup', 
    description: 'Configure property type and checklist modules',
    order: 1 
  },
  initial_screen: { 
    label: 'Initial Screen', 
    description: 'Quick assessment to identify obvious flags',
    order: 2 
  },
  deep_diligence: { 
    label: 'Deep Due Diligence', 
    description: 'Thorough investigation of all checklist items',
    order: 3 
  },
  final_analysis: { 
    label: 'Final Analysis', 
    description: 'Financial modeling and final review',
    order: 4 
  },
  decision: { 
    label: 'Decision', 
    description: 'Make Go/No-Go decision',
    order: 5 
  },
};

export const DECISION_CONFIG: Record<FinalDecision, {
  label: string;
  description: string;
  color: string;
}> = {
  go: { 
    label: 'Go', 
    description: 'Proceed with acquisition',
    color: '#22C55E' 
  },
  no_go: { 
    label: 'No Go', 
    description: 'Do not proceed - deal killed',
    color: '#EF4444' 
  },
  pass_for_now: { 
    label: 'Pass for Now', 
    description: 'Not proceeding currently, may revisit',
    color: '#F59E0B' 
  },
};

// =============================================================================
// Opportunity Types (land classification)
// =============================================================================

export type BasePropertyType = 
  | 'raw_land'
  | 'improved_land'
  | 'residential'
  | 'agricultural'
  | 'timber'
  | 'recreational'
  | 'mixed_use';

export const OPPORTUNITY_TYPE_CONFIG: Record<BasePropertyType, {
  label: string;
  description: string;
  defaultModules: ModuleCode[];
}> = {
  raw_land: {
    label: 'Raw Land',
    description: 'Unimproved land with no structures',
    defaultModules: ['land_basics', 'soils', 'access', 'legal', 'financials'],
  },
  improved_land: {
    label: 'Improved Land',
    description: 'Land with some improvements (not residential)',
    defaultModules: ['land_basics', 'soils', 'access', 'utilities', 'legal', 'financials'],
  },
  residential: {
    label: 'Residential',
    description: 'Primary use is housing',
    defaultModules: ['land_basics', 'residential_structure', 'utilities', 'septic', 'legal', 'financials'],
  },
  agricultural: {
    label: 'Agricultural/Farm',
    description: 'Active or potential farming use',
    defaultModules: ['land_basics', 'soils', 'agricultural', 'water', 'fencing', 'legal', 'financials'],
  },
  timber: {
    label: 'Timber',
    description: 'Primary value is timber',
    defaultModules: ['land_basics', 'timber', 'access', 'legal', 'financials'],
  },
  recreational: {
    label: 'Recreational',
    description: 'Hunting, fishing, camping focus',
    defaultModules: ['land_basics', 'water_features', 'wildlife', 'access', 'legal', 'financials'],
  },
  mixed_use: {
    label: 'Mixed Use',
    description: 'Multiple significant uses',
    defaultModules: ['land_basics', 'legal', 'financials'],
  },
};

// Alias for backward compatibility
export const PROPERTY_TYPE_CONFIG = OPPORTUNITY_TYPE_CONFIG;

// =============================================================================
// Module Types
// =============================================================================

export type ModuleCode = 
  | 'land_basics'
  | 'soils'
  | 'water'
  | 'septic'
  | 'utilities'
  | 'access'
  | 'legal'
  | 'residential_structure'
  | 'outbuildings'
  | 'fencing'
  | 'timber'
  | 'agricultural'
  | 'water_features'
  | 'wildlife'
  | 'environmental'
  | 'flood_climate'
  | 'proximity'
  | 'financials'
  | 'multiple_parcels'
  | 'neighbors'
  | 'property_visit';

export interface ModuleConfig {
  code: ModuleCode;
  name: string;
  description: string;
  icon: string; // Ionicons name
  isAlwaysIncluded: boolean;
  applicableTo: BasePropertyType[] | 'all';
}

export const MODULE_CONFIG: Record<ModuleCode, ModuleConfig> = {
  land_basics: {
    code: 'land_basics',
    name: 'Land Basics',
    description: 'Property identification, ownership, zoning, and boundaries',
    icon: 'map-outline',
    isAlwaysIncluded: true,
    applicableTo: 'all',
  },
  soils: {
    code: 'soils',
    name: 'Soils & Land Use',
    description: 'Soil analysis, topography, and land use suitability',
    icon: 'leaf-outline',
    isAlwaysIncluded: false,
    applicableTo: ['raw_land', 'improved_land', 'agricultural', 'timber'],
  },
  water: {
    code: 'water',
    name: 'Water Resources',
    description: 'Wells, springs, water quality, and water systems',
    icon: 'water-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  septic: {
    code: 'septic',
    name: 'Septic/Sewer',
    description: 'Septic systems, municipal sewer, and waste management',
    icon: 'construct-outline',
    isAlwaysIncluded: false,
    applicableTo: ['residential', 'improved_land', 'agricultural'],
  },
  utilities: {
    code: 'utilities',
    name: 'Utilities',
    description: 'Electric, gas, internet, and communications',
    icon: 'flash-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  access: {
    code: 'access',
    name: 'Access & Roads',
    description: 'Road access, easements, and driveways',
    icon: 'car-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  legal: {
    code: 'legal',
    name: 'Legal & Easements',
    description: 'Easements, deed restrictions, rights, and legal issues',
    icon: 'document-text-outline',
    isAlwaysIncluded: true,
    applicableTo: 'all',
  },
  residential_structure: {
    code: 'residential_structure',
    name: 'Residential Structure',
    description: 'House inspection, systems, and condition',
    icon: 'home-outline',
    isAlwaysIncluded: false,
    applicableTo: ['residential', 'agricultural', 'mixed_use'],
  },
  outbuildings: {
    code: 'outbuildings',
    name: 'Outbuildings',
    description: 'Barns, sheds, shops, and other structures',
    icon: 'business-outline',
    isAlwaysIncluded: false,
    applicableTo: ['agricultural', 'residential', 'improved_land', 'mixed_use'],
  },
  fencing: {
    code: 'fencing',
    name: 'Fencing & Boundaries',
    description: 'Perimeter fencing, cross-fencing, and gates',
    icon: 'grid-outline',
    isAlwaysIncluded: false,
    applicableTo: ['agricultural', 'recreational', 'residential'],
  },
  timber: {
    code: 'timber',
    name: 'Timber',
    description: 'Timber inventory, value, and management',
    icon: 'leaf-outline',
    isAlwaysIncluded: false,
    applicableTo: ['timber', 'agricultural', 'recreational', 'raw_land'],
  },
  agricultural: {
    code: 'agricultural',
    name: 'Agricultural',
    description: 'Farming history, productivity, and infrastructure',
    icon: 'nutrition-outline',
    isAlwaysIncluded: false,
    applicableTo: ['agricultural'],
  },
  water_features: {
    code: 'water_features',
    name: 'Water Features',
    description: 'Ponds, creeks, wetlands, and springs',
    icon: 'fish-outline',
    isAlwaysIncluded: false,
    applicableTo: ['recreational', 'agricultural', 'raw_land'],
  },
  wildlife: {
    code: 'wildlife',
    name: 'Wildlife & Recreation',
    description: 'Game populations, hunting, fishing potential',
    icon: 'paw-outline',
    isAlwaysIncluded: false,
    applicableTo: ['recreational', 'timber', 'agricultural'],
  },
  environmental: {
    code: 'environmental',
    name: 'Environmental',
    description: 'Environmental assessments, contamination, protected species',
    icon: 'alert-circle-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  flood_climate: {
    code: 'flood_climate',
    name: 'Flood & Climate',
    description: 'Flood zones, fire risk, weather concerns',
    icon: 'rainy-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  proximity: {
    code: 'proximity',
    name: 'Proximity & Location',
    description: 'Distance to services, towns, and amenities',
    icon: 'location-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  financials: {
    code: 'financials',
    name: 'Financial Analysis',
    description: 'Purchase analysis, costs, income, and investment returns',
    icon: 'calculator-outline',
    isAlwaysIncluded: true,
    applicableTo: 'all',
  },
  multiple_parcels: {
    code: 'multiple_parcels',
    name: 'Multiple Parcels',
    description: 'Multi-parcel property considerations',
    icon: 'layers-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  neighbors: {
    code: 'neighbors',
    name: 'Neighbors & Community',
    description: 'Adjacent properties, community, nuisances',
    icon: 'people-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
  property_visit: {
    code: 'property_visit',
    name: 'Property Visit',
    description: 'On-site inspection and walkthrough',
    icon: 'walk-outline',
    isAlwaysIncluded: false,
    applicableTo: 'all',
  },
};

// =============================================================================
// Data Field Types
// =============================================================================

export type DataFieldType = 'string' | 'number' | 'boolean' | 'date' | 'array' | 'select' | 'multiselect';

export interface DataFieldDefinition {
  key: string;
  label: string;
  type: DataFieldType;
  required?: boolean;
  unit?: string;
  placeholder?: string;
  options?: { value: string; label: string }[]; // For select/multiselect
  min?: number;
  max?: number;
}

// =============================================================================
// Checklist Item Types
// =============================================================================

export interface ChecklistItemTemplate {
  code: string;
  title: string;
  description?: string;
  isGroupTask: boolean;
  dataFields?: DataFieldDefinition[];
  suggestedFlags?: {
    flag: FlagType;
    condition: string;
  }[];
}

export interface ChecklistCategory {
  code: string;
  name: string;
  items: ChecklistItemTemplate[];
}

export interface ChecklistModuleDefinition {
  code: ModuleCode;
  name: string;
  description: string;
  categories: ChecklistCategory[];
}

// =============================================================================
// Instance Types (Database Records)
// =============================================================================

export interface DueDiligenceItem {
  id: number;
  dueDiligenceId: number;
  code: string;
  module: ModuleCode;
  category: string;
  title: string;
  description?: string;
  isGroupTask: boolean;
  
  // Assignment
  assignedTo: number[]; // User IDs
  
  // Status
  status: ItemStatus;
  flag?: FlagType;
  
  // Data
  notes?: string;
  dataFields: Record<string, any>;
  attachments: DueDiligenceAttachment[];
  
  // Metadata
  completedAt?: string;
  completedBy?: number;
  createdAt: string;
  updatedAt: string;
}

export interface DueDiligenceAttachment {
  id: number;
  itemId: number;
  filename: string;
  fileUrl: string;
  fileType?: string;
  uploadedAt: string;
  uploadedBy: number;
}

export interface DueDiligence {
  id: number;
  opportunityId: number;
  
  // Configuration
  basePropertyType: BasePropertyType;
  enabledModules: ModuleCode[];
  
  // Workflow
  currentPhase: WorkflowPhase;
  finalDecision?: FinalDecision;
  decisionRationale?: string;
  decisionDate?: string;
  
  // Items (populated on fetch)
  items?: DueDiligenceItem[];
  
  // Computed summaries
  flagSummary?: {
    big_red: number;
    red: number;
    yellow: number;
    green: number;
    big_green: number;
    clear: number;
    pending: number;
    not_applicable: number;
  };
  statusSummary?: {
    pending: number;
    in_progress: number;
    complete: number;
    not_applicable: number;
  };
  completionPercent?: number;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: number;
}

// =============================================================================
// API Request/Response Types
// =============================================================================

export interface CreateDueDiligenceRequest {
  opportunityId: number;
  basePropertyType: BasePropertyType;
  enabledModules: ModuleCode[];
}

export interface UpdateDueDiligenceRequest {
  basePropertyType?: BasePropertyType;
  enabledModules?: ModuleCode[];
  currentPhase?: WorkflowPhase;
  finalDecision?: FinalDecision;
  decisionRationale?: string;
}

export interface UpdateDueDiligenceItemRequest {
  status?: ItemStatus;
  flag?: FlagType;
  notes?: string;
  dataFields?: Record<string, any>;
  assignedTo?: number[];
}

export interface DueDiligenceResponse {
  dueDiligence: DueDiligence;
}

export interface DueDiligenceListResponse {
  dueDiligences: DueDiligence[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get all modules that should be enabled by default for an opportunity type
 */
export function getDefaultModules(opportunityType: BasePropertyType): ModuleCode[] {
  return OPPORTUNITY_TYPE_CONFIG[opportunityType].defaultModules;
}

/**
 * Get all always-included modules
 */
export function getRequiredModules(): ModuleCode[] {
  return Object.values(MODULE_CONFIG)
    .filter(m => m.isAlwaysIncluded)
    .map(m => m.code);
}

/**
 * Check if a module is applicable to an opportunity type
 */
export function isModuleApplicable(module: ModuleCode, opportunityType: BasePropertyType): boolean {
  const config = MODULE_CONFIG[module];
  if (config.applicableTo === 'all') return true;
  return config.applicableTo.includes(opportunityType);
}

/**
 * Get sorted flags by priority
 */
export function sortByFlagPriority(items: DueDiligenceItem[]): DueDiligenceItem[] {
  return [...items].sort((a, b) => {
    const priorityA = a.flag ? FLAG_CONFIG[a.flag].priority : 999;
    const priorityB = b.flag ? FLAG_CONFIG[b.flag].priority : 999;
    return priorityA - priorityB;
  });
}

/**
 * Calculate completion percentage
 */
export function calculateCompletion(items: DueDiligenceItem[]): number {
  if (items.length === 0) return 0;
  const completedOrNA = items.filter(
    i => i.status === 'complete' || i.status === 'not_applicable'
  ).length;
  return Math.round((completedOrNA / items.length) * 100);
}

/**
 * Calculate flag summary
 */
export function calculateFlagSummary(items: DueDiligenceItem[]): DueDiligence['flagSummary'] {
  const summary: DueDiligence['flagSummary'] = {
    big_red: 0,
    red: 0,
    yellow: 0,
    green: 0,
    big_green: 0,
    clear: 0,
    pending: 0,
    not_applicable: 0,
  };
  
  items.forEach(item => {
    if (item.flag && summary[item.flag] !== undefined) {
      summary[item.flag]++;
    }
  });
  
  return summary;
}

/**
 * Calculate status summary
 */
export function calculateStatusSummary(items: DueDiligenceItem[]): DueDiligence['statusSummary'] {
  const summary: DueDiligence['statusSummary'] = {
    pending: 0,
    in_progress: 0,
    complete: 0,
    not_applicable: 0,
  };
  
  items.forEach(item => {
    summary[item.status]++;
  });
  
  return summary;
}
